// Compras y gastos — DATOS REALES por cooperativa: gastos pagados de contado, causación de
// compras (con la retención en la fuente que fija la configuración), pago a proveedores y pago
// de retenciones. Todo pasa por el motor contable. Antes usaba datos de ejemplo.

import React, { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import EncabezadoOperativo from '../components/EncabezadoOperativo';
import FormularioDialog from '../components/FormularioDialog';
import TablaDatos from '../components/TablaDatos';
import useCargar from '../hooks/useCargar';
import useCooperativaOperativa from '../hooks/useCooperativaOperativa';
import { contabilidadApi, gastosApi, tercerosApi } from '../services/coopnetApi';
import { hoy } from '../utils/formato';

const ETIQUETA_GASTO = { honorarios: 'Honorarios', arrendamiento: 'Arrendamientos', compra_general: 'Compra general / diversos' };

const Compras = () => {
  const op = useCooperativaOperativa();
  const id = op.cooperativaId;
  const [dlg, setDlg] = useState(null);
  const [ok, setOk] = useState('');

  const { datos, cargando, error, recargar } = useCargar(async () => {
    if (!id) return null;
    const [proveedores, refs] = await Promise.all([tercerosApi.listar(id, 'Proveedor'), contabilidadApi.referencias(id)]);
    return { proveedores, retenciones: refs.retenciones || [] };
  }, [id]);

  const proveedores = datos?.proveedores || [];
  const retenciones = datos?.retenciones || [];
  const optProveedor = proveedores.map((p) => ({ valor: p.id, etiqueta: `${p.nombre} — ${p.numeroDocumento}` }));
  const optGasto = retenciones.map((r) => ({ valor: r.tipoGasto, etiqueta: `${ETIQUETA_GASTO[r.tipoGasto] || r.tipoGasto} (retención ${r.tasaPct}%)` }));
  const medio = { campo: 'medioPagoCaja', label: 'Pagado en efectivo (caja). Si no, por bancos', tipo: 'switch' };
  const prov = { campo: 'terceroId', label: 'Proveedor', tipo: 'select', requerido: true, opciones: optProveedor };

  const acciones = [
    ['Registrar gasto', 'Gasto pagado de contado a un proveedor.', 'gasto'],
    ['Causar compra', 'Registra la factura y la retención en la fuente; queda por pagar.', 'compra'],
    ['Pagar a proveedor', 'Abona una cuenta por pagar.', 'proveedor'],
    ['Pagar retención en la fuente', 'Paga a la DIAN lo retenido.', 'retencion'],
  ];

  return (
    <EncabezadoOperativo
      titulo="Compras y gastos"
      subtitulo="Proveedores, gastos y retenciones"
      operativa={op}
      acciones={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setDlg('nuevoProveedor')}>Nuevo proveedor</Button>}
    >
      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>{ok}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {cargando && !datos ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            {retenciones.map((r) => <Chip key={r.tipoGasto} variant="outlined" label={`${ETIQUETA_GASTO[r.tipoGasto] || r.tipoGasto}: retención ${r.tasaPct}%`} />)}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 2, mb: 4 }}>
            {acciones.map(([titulo, texto, clave]) => (
              <Card key={clave} variant="outlined"><CardContent>
                <Typography variant="subtitle1" fontWeight={700}>{titulo}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>{texto}</Typography>
                <Button size="small" variant="outlined" disabled={clave !== 'retencion' && proveedores.length === 0} onClick={() => setDlg(clave)}>Registrar</Button>
              </CardContent></Card>
            ))}
          </Box>
          <Typography variant="h6" gutterBottom>Proveedores</Typography>
          <TablaDatos
            filas={proveedores}
            vacio="Aún no hay proveedores. Registra uno con «Nuevo proveedor» para poder causar compras y gastos."
            columnas={[
              { titulo: 'Proveedor', render: (p) => p.nombre },
              { titulo: 'Documento', render: (p) => `${p.tipoDocumento} ${p.numeroDocumento}` },
            ]}
          />
        </>
      )}

      <FormularioDialog
        abierto={dlg === 'nuevoProveedor'}
        titulo="Nuevo proveedor"
        campos={[
          { campo: 'nombre', label: 'Nombre o razón social', tipo: 'texto', requerido: true },
          { campo: 'tipoDocumento', label: 'Tipo de documento', tipo: 'select', requerido: true, opciones: [{ valor: 'NIT', etiqueta: 'NIT' }, { valor: 'CC', etiqueta: 'Cédula de ciudadanía' }, { valor: 'CE', etiqueta: 'Cédula de extranjería' }] },
          { campo: 'numeroDocumento', label: 'Número de documento', tipo: 'texto', requerido: true },
        ]}
        inicial={{ tipoDocumento: 'NIT' }}
        textoBoton="Registrar proveedor"
        onGuardar={async (v) => { await tercerosApi.crearProveedor(id, v); setOk(`Proveedor ${v.nombre} registrado.`); recargar(); }}
        onClose={() => setDlg(null)}
      />
      <FormularioDialog
        abierto={dlg === 'gasto'}
        titulo="Registrar gasto"
        campos={[{ campo: 'tipoGasto', label: 'Tipo de gasto', tipo: 'select', requerido: true, opciones: optGasto }, prov, { campo: 'monto', label: 'Monto', tipo: 'numero', requerido: true, positivo: true }, { campo: 'fecha', label: 'Fecha', tipo: 'fecha', requerido: true }, medio]}
        inicial={{ fecha: hoy(), medioPagoCaja: false }}
        textoBoton="Registrar"
        onGuardar={async (v) => { await gastosApi.registrarGasto(id, v); setOk('Gasto registrado.'); }}
        onClose={() => setDlg(null)}
      />
      <FormularioDialog
        abierto={dlg === 'compra'}
        titulo="Causar compra"
        campos={[{ campo: 'tipoGasto', label: 'Tipo de gasto', tipo: 'select', requerido: true, opciones: optGasto }, prov, { campo: 'montoBruto', label: 'Valor bruto de la factura', tipo: 'numero', requerido: true, positivo: true }, { campo: 'fecha', label: 'Fecha', tipo: 'fecha', requerido: true }]}
        inicial={{ fecha: hoy() }}
        textoBoton="Causar"
        onGuardar={async (v) => { await gastosApi.causarCompra(id, v); setOk('Compra causada; queda una cuenta por pagar al proveedor y la retención por pagar.'); }}
        onClose={() => setDlg(null)}
      />
      <FormularioDialog
        abierto={dlg === 'proveedor'}
        titulo="Pagar a proveedor"
        campos={[prov, { campo: 'monto', label: 'Monto', tipo: 'numero', requerido: true, positivo: true }, { campo: 'fecha', label: 'Fecha', tipo: 'fecha', requerido: true }, medio]}
        inicial={{ fecha: hoy(), medioPagoCaja: false }}
        textoBoton="Registrar pago"
        onGuardar={async (v) => { await gastosApi.pagarProveedor(id, v); setOk('Pago a proveedor registrado.'); }}
        onClose={() => setDlg(null)}
      />
      <FormularioDialog
        abierto={dlg === 'retencion'}
        titulo="Pagar retención en la fuente"
        campos={[{ campo: 'monto', label: 'Monto', tipo: 'numero', requerido: true, positivo: true }, { campo: 'fecha', label: 'Fecha', tipo: 'fecha', requerido: true }, medio]}
        inicial={{ fecha: hoy(), medioPagoCaja: false }}
        textoBoton="Registrar pago"
        onGuardar={async (v) => { await gastosApi.pagarRetencion(id, v); setOk('Pago de retención registrado.'); }}
        onClose={() => setDlg(null)}
      />
    </EncabezadoOperativo>
  );
};

export default Compras;
